"use client";

import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@repo/ui/components/ui/select";
import { Field, FieldError, FieldLabel } from "@repo/ui/form-fields";
import { Tags } from "lucide-react";
import { useFieldContext } from "@/shared/forms/form-context";

export interface VisitTypeOption {
  code: string;
  display: string;
}

export interface VisitTypeFieldProps {
  label?: string;
  placeholder?: string;
  visitTypes: VisitTypeOption[];
  disabled?: boolean;
  isLoading?: boolean;
}

export function VisitTypeField({
  label,
  placeholder = "Select visit type...",
  visitTypes,
  disabled,
  isLoading,
}: VisitTypeFieldProps) {
  const field = useFieldContext<string>();
  const itemsMap = Object.fromEntries(visitTypes.map((vt) => [vt.code, vt.display]));
  const displayPlaceholder = isLoading ? "Loading..." : placeholder;

  return (
    <Field errors={field.state.meta.errors} isTouched={field.state.meta.isTouched}>
      {label && <FieldLabel>{label}</FieldLabel>}
      <Select
        value={field.state.value || null}
        onValueChange={(value) => {
          field.handleChange(value ?? "");
          field.handleBlur();
        }}
        disabled={disabled || isLoading}
        required
        items={itemsMap}
      >
        <SelectTrigger className={`w-full ${label ? "mt-1" : ""}`} disabled={disabled || isLoading}>
          <SelectValue>
            {(value) => {
              if (value == null) return displayPlaceholder;
              return itemsMap[value as string] ?? String(value);
            }}
          </SelectValue>
        </SelectTrigger>
        <SelectContent>
          {visitTypes.length === 0 ? (
            <div className="flex items-center gap-2 px-2 py-4 text-sm text-muted-foreground justify-center">
              <Tags className="h-4 w-4" />
              No visit types configured
            </div>
          ) : (
            visitTypes.map((vt) => (
              <SelectItem key={vt.code} value={vt.code}>
                {vt.display}
              </SelectItem>
            ))
          )}
        </SelectContent>
      </Select>
      <FieldError />
    </Field>
  );
}
